import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, StatCard, EmptyState } from "@/components/common";
import { minutesToHours, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Play, Square, Clock, Plus, Timer } from "lucide-react";

export default function TimeTracking() {
  const qc = useQueryClient();
  const [elapsed, setElapsed] = useState(0);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState({ task_id: "", project_id: "", description: "", minutes: 30 });

  const { data: active } = useQuery({ queryKey: ["timer"], queryFn: async () => (await api.get("/timer/active")).data, refetchInterval: 5000 });
  const { data: entries = [] } = useQuery({ queryKey: ["time-entries"], queryFn: async () => { const d = (await api.get("/time-entries")).data; return Array.isArray(d) ? d : []; } });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => { const d = (await api.get("/projects")).data; return Array.isArray(d) ? d : []; } });
  const { data: tasks = [] } = useQuery({ queryKey: ["tasks"], queryFn: async () => { const d = (await api.get("/tasks", { params: { mine: true } })).data; return Array.isArray(d) ? d : []; } });

  useEffect(() => {
    if (!active) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now() - new Date(active.start_time).getTime()) / 1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [active]);

  const start = async () => { try { await api.post("/timer/start", {}); qc.invalidateQueries({ queryKey: ["timer"] }); toast.success("Zamanlayıcı başladı"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const stop = async () => { const { data } = await api.post("/timer/stop"); toast.success(`${data.minutes} dk kaydedildi`); qc.invalidateQueries(); };
  const addManual = async () => {
    try { await api.post("/time-entries", { ...manual, task_id: manual.task_id || null, project_id: manual.project_id || null, minutes: Number(manual.minutes) }); toast.success("Zaman kaydı eklendi"); qc.invalidateQueries({ queryKey: ["time-entries"] }); setManualOpen(false); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const hh = String(Math.floor(elapsed / 3600)).padStart(2, "0");
  const mm = String(Math.floor((elapsed % 3600) / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");
  const totalToday = entries.filter((e) => (e.created_at || "").slice(0, 10) === new Date().toISOString().slice(0, 10)).reduce((s, e) => s + (e.minutes || 0), 0);
  const totalAll = entries.reduce((s, e) => s + (e.minutes || 0), 0);
  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));

  return (
    <div>
      <PageHeader title="Zaman Takibi" subtitle="Görev ve projeleriniz için zaman kaydı"
        actions={<Button variant="outline" onClick={() => setManualOpen(true)} data-testid="manual-time-button"><Plus className="h-4 w-4 mr-1.5" /> Manuel Giriş</Button>} />

      <Card className="p-6 mb-6 flex flex-col md:flex-row items-center gap-6">
        <div className="text-5xl font-mono font-bold tabular-nums">{hh}:{mm}:{ss}</div>
        <div className="flex-1 text-center md:text-left">
          <p className="text-sm text-muted-foreground">{active ? "Zamanlayıcı çalışıyor" : "Zamanlayıcı durduruldu"}</p>
          {active?.description && <p className="text-sm font-medium">{active.description}</p>}
        </div>
        {active ? (
          <Button size="lg" variant="destructive" onClick={stop} data-testid="timer-stop"><Square className="h-4 w-4 mr-2" /> Durdur</Button>
        ) : (
          <Button size="lg" onClick={start} data-testid="timer-start"><Play className="h-4 w-4 mr-2" /> Başlat</Button>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <StatCard icon={Clock} label="Bugün" value={minutesToHours(totalToday)} tone="indigo" />
        <StatCard icon={Timer} label="Toplam" value={minutesToHours(totalAll)} tone="emerald" />
      </div>

      <Card>
        <div className="p-4 border-b font-semibold">Zaman Kayıtları</div>
        <div className="divide-y">
          {entries.length ? entries.map((e) => (
            <div key={e.id} className="flex items-center gap-3 p-3.5">
              <div className="flex-1"><p className="text-sm font-medium">{e.description || projMap[e.project_id] || "Zaman kaydı"}</p><p className="text-xs text-muted-foreground">{fmtDate(e.created_at)}</p></div>
              {e.billable && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Faturalanabilir</Badge>}
              <span className="text-sm font-semibold">{minutesToHours(e.minutes)}</span>
            </div>
          )) : <EmptyState icon={Clock} title="Zaman kaydı yok" />}
        </div>
      </Card>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent data-testid="manual-time-dialog">
          <DialogHeader><DialogTitle>Manuel Zaman Girişi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Açıklama</Label><Input value={manual.description} onChange={(e) => setManual({ ...manual, description: e.target.value })} className="mt-1.5" data-testid="manual-desc-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Süre (dakika)</Label><Input type="number" value={manual.minutes} onChange={(e) => setManual({ ...manual, minutes: e.target.value })} className="mt-1.5" data-testid="manual-minutes-input" /></div>
              <div><Label>Proje</Label>
                <Select value={manual.project_id || "none"} onValueChange={(v) => setManual({ ...manual, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Yok</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setManualOpen(false)}>İptal</Button><Button onClick={addManual} data-testid="manual-save-button">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
